import { useState } from 'react';
import { Card } from '@material-ui/core';
import { useSelector } from 'react-redux';
import Autocomplete from '@material-ui/lab/Autocomplete';
import PropTypes from 'prop-types';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import { lighten, makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import TableRow from '@material-ui/core/TableRow';
import Table from '@material-ui/core/Table';
import Toolbar from '@material-ui/core/Toolbar';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Tooltip from '@material-ui/core/Tooltip';
import Paper from '@material-ui/core/Paper';
import TableContainer from '@material-ui/core/TableContainer';
import TableBody from '@material-ui/core/TableBody';
import TablePagination from '@material-ui/core/TablePagination';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useDispatch } from 'react-redux';
import {
  FLASHCARD_TYPE_CODING,
  FLASHCARD_TYPE_ENUMERATION,
  FLASHCARD_TYPE_IMAGE,
  FLASHCARD_TYPE_IMAGEPOS,
  FLASHCARD_TYPE_SIMPLE,
  FLASHCARD_TYPE_VOCABLE,
  FLASHCARD_TYPE_QUIZ,
} from '../../constants/flashcardTypes';
import { deleteFlashcard } from '../../actions/flashcards.js';
// import testIntervalsInSecs from '../Trainings/Testform';
// example https://codesandbox.io/s/f71wj?file=/demo.js:9610-9615

export const testIntervals = [
  '0',
  '1 day',
  '1 day',
  '2 days',
  '3 days',
  '5 days',
  '8 days',
  '13 days',
  '21 days',
  '34 days',
  '55 days',
  '89 days',
  '144 days',
  '233 days',
  '377 days',
  '610 days',
  '987 days',
  '1597 days',
  '2584 days',
  '4181 days',
];

export const testIntervalsInSecs = [
  0, 72000, 72000, 158400, 244800, 417600, 676800, 1108800, 1800000, 2923200,
  4737600, 7675200, 9835200, 20116800, 29102400, 52689600, 85262400, 137966400,
  223243200, 361224000,
  // testIntervals in seconds (-4 hours)
];

const headCells = [
  { id: 'title', numeric: false, disablePadding: false, label: 'Title' },
  {
    id: 'trainings',
    numeric: false,
    disablePadding: false,
    label: 'Trainings',
  },
  {
    id: 'passlevel',
    numeric: true,
    disablePadding: false,
    label: 'Level',
  },
  {
    id: 'solution',
    numeric: false,
    disablePadding: false,
    label: 'Solution',
  },
  { id: 'type', numeric: false, disablePadding: false, label: 'Type' },
  {
    id: 'edit',
    numeric: false,
    disablePadding: false,
    label: 'Edit',
  },
  {
    id: 'del',
    numeric: false,
    disablePadding: false,
    label: 'Delete',
  },
];

function solutionSortKey(row) {
  if (!row.solutions || !row.solutions.length) {
    return '';
  }
  if (row.header.type === FLASHCARD_TYPE_QUIZ) {
    return row.solutions
      .map((s) => s.text || '')
      .join('\n')
      .toLowerCase();
  }
  return (row.solutions[0].text || '').toLowerCase();
}

function descendingComparator(a, b, orderBy) {
  switch (orderBy) {
    case 'solution':
      if (solutionSortKey(b) < solutionSortKey(a)) {
        return -1;
      }
      if (solutionSortKey(b) > solutionSortKey(a)) {
        return 1;
      }
    case 'title':
      if (b.header[orderBy].toLowerCase() < a.header[orderBy].toLowerCase()) {
        return -1;
      }
      if (b.header[orderBy].toLowerCase() > a.header[orderBy].toLowerCase()) {
        return 1;
      }
    case 'trainings':
      if (b.header[orderBy].length === 0) {
        return -1;
      }
      if (a.header[orderBy].length === 0) {
        return 1;
      }
      if (
        b.header[orderBy][0].toLowerCase() < a.header[orderBy][0].toLowerCase()
      ) {
        return -1;
      }
      if (
        b.header[orderBy][0].toLowerCase() > a.header[orderBy][0].toLowerCase()
      ) {
        return 1;
      }
    case 'passlevel':
    default:
      if (b.header[orderBy] < a.header[orderBy]) {
        return -1;
      }
      if (b.header[orderBy] > a.header[orderBy]) {
        return 1;
      }
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function EnhancedTableHead(props) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ 'aria-label': 'select all desserts' }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === 'light'
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.secondary.dark,
        },
  title: {
    flex: '1 1 100%',
  },
}));

const EnhancedTableToolbar = (props) => {
  const classes = useToolbarStyles();
  const { numSelected } = props;

  return (
    <Toolbar
      className={clsx(classes.root, {
        [classes.highlight]: props.numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <Typography
          className={classes.title}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {props.numSelected} selected
        </Typography>
      ) : (
        <Typography
          className={classes.title}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          {props.numAvailable} flashcards
        </Typography>
      )}

      {props.numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
  numAvailable: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

function Flashcardselection({ flashcardHeaders, setCurrentId }) {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filterTitle, setFilterTitle] = useState('');
  const [filterSolution, setFilterSolution] = useState('');
  const [filterTrainings, setFilterTrainings] = useState('');
  const [filterType, setFilterType] = useState('');
  const trainings = useSelector((state) => state.trainings);
  const [filterTrainingless, setFilterTrainingless] = useState(false);
  const [filterSoundless, setFilterSoundless] = useState(false);

  let rows = flashcardHeaders;
  if (filterType != '') {
    rows = rows.filter((flashcard) =>
      filterType.includes(flashcard.header.type),
    );
  }

  if (filterTrainings != '') {
    rows = rows.filter((flashcard) =>
      filterTrainings.some((training) =>
        flashcard.header.trainings.includes(training),
      ),
    );
  }

  if (filterTitle != '') {
    rows = rows.filter((flashcard) =>
      flashcard.header.title.toLowerCase().includes(filterTitle.toLowerCase()),
    );
  }

  if (filterSoundless != '') {
    rows = rows.filter((flashcard) => {
      if (flashcard.header.type === FLASHCARD_TYPE_VOCABLE) {
        if (flashcard.header.soundavailable === true) {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    });
  }

  if (filterTrainingless != '') {
    rows = rows.filter((flashcard) => {
      return (
        flashcard !== undefined &&
        flashcard.header !== undefined &&
        (flashcard.header.trainings.length === 0 ||
          flashcard.header.trainings[0] === '')
      );
    });
  }

  // needs to be fixed for all kind of solutions
  if (filterSolution != '') {
    rows = rows.filter((flashcard) => {
      const q = filterSolution.toLowerCase();
      if (flashcard.header.type === FLASHCARD_TYPE_QUIZ) {
        return (flashcard.solutions || [])
          .map((s) => s.text || '')
          .join(' ')
          .toLowerCase()
          .includes(q);
      }
      return flashcard.solutions[0].text.toLowerCase().includes(q);
    });
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n._id);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  function timeDiffCalc(dateFuture, dateNow) {
    let diffInMilliSeconds = Math.abs(dateFuture - dateNow) / 1000;

    // calculate days
    const days = Math.floor(diffInMilliSeconds / 86400);
    diffInMilliSeconds -= days * 86400;

    // calculate hours
    const hours = Math.floor(diffInMilliSeconds / 3600) % 24;
    diffInMilliSeconds -= hours * 3600;

    // calculate minutes
    const minutes = Math.floor(diffInMilliSeconds / 60) % 60;
    diffInMilliSeconds -= minutes * 60;

    let difference = '';
    if (days > 0) {
      difference += days === 1 ? `${days} day, ` : `${days} days, `;
    }

    difference +=
      hours === 0 || hours === 1 ? `${hours} hour, ` : `${hours} hours, `;

    difference +=
      minutes === 0 || hours === 1
        ? `${minutes} minutes`
        : `${minutes} minutes`;

    return difference;
  }

  return (
    <Card style={{ margin: '10px', display: 'inline-block' }}>
      <Paper className={classes.paper}>
        <Checkbox
          onClick={(event) => setFilterSoundless(event.target.checked)}
          checked={filterSoundless}
        />
        filter soundless vocable flashcards<p></p>
        <Checkbox
          onClick={(event) => setFilterTrainingless(event.target.checked)}
          checked={filterTrainingless}
        />
        filter trainingless flashcards
        <EnhancedTableToolbar
          numSelected={selected.length}
          numAvailable={rows.length}
        />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000, 2000]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
        <TableContainer>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <TextField
                    value={filterTitle}
                    onChange={(e) => {
                      setFilterTitle(e.target.value);
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Autocomplete
                    multiple
                    id="tags-standard"
                    options={trainings.map((training) => training.name)}
                    // defaultValue={defaultTrainingValue}
                    onChange={(event, values) => {
                      event.preventDefault();
                      setFilterTrainings(values.map((value) => value));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label=""
                        placeholder="select training name"
                      />
                    )}
                  />
                </TableCell>
                <TableCell></TableCell>
                <TableCell>
                  <TextField
                    onChange={(e) => setFilterSolution(e.target.value)}
                  >
                    {filterSolution}
                  </TextField>
                </TableCell>
                <TableCell>
                  <Autocomplete
                    multiple
                    id="tags-type"
                    options={flashcardHeaders
                      .map((flashcard) => flashcard.header.type)
                      .filter((v, i, a) => a.indexOf(v) === i)}
                    onChange={(event, values) => {
                      event.preventDefault();
                      setFilterType(values.map((value) => value));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label=""
                        placeholder="select type name"
                      />
                    )}
                  />
                </TableCell>
              </TableRow>
              {stableSort(rows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const isItemSelected = isSelected(row._id);
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      // key={row.name}
                      selected={isItemSelected}
                      key={`${row.header.title}_title_row${row._id}`}
                      style={{
                        width: '1px',
                        whiteSpace: 'nowrap',
                        padding: 0,
                        height: 10,
                        background:
                          Date.parse(row.header.lastpass) +
                            testIntervalsInSecs[row.header.passlevel] * 1000 <
                            Date.parse(new Date().toISOString()) ||
                          row.header.passlevel === 0
                            ? '#ffeeee'
                            : '#edffef',
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={(event) => handleClick(event, row._id)}
                          checked={isItemSelected}
                          inputProps={{ 'aria-labelledby': labelId }}
                        />
                      </TableCell>

                      <TableCell
                        key={`${row._id}_title`}
                        style={{ padding: '5px' }}
                        component="th"
                        id={labelId}
                        scope="row"
                        // padding="none"
                      >
                        <Tooltip
                          title={
                            row.header.title
                              ? row.header.title
                              : 'placeholder title'
                          }
                          arrow
                        >
                          <div
                            style={{
                              color: row.header.active ? '' : 'gray',
                            }}
                          >
                            {/* {row.type === FLASHCARD_TYPE_IMAGE ? (
                              <img
                                src={
                                  row.inputimage ||
                                  'https://user-images.githubusercontent.com/194400/49531010-48dad180-f8b1-11e8-8d89-1e61320e1d82.png'
                                }
                                height="30"
                              />
                            ) : row.title !== undefined &&
                              row.title.length > 30 ? (
                              row.title.substring(0, 30) + ' ...'
                            ) : (
                              row.title
                            )} */}
                            {row.header.title !== undefined
                              ? row.header.title.length > 40
                                ? row.header.title.substring(0, 40) + ' ...'
                                : row.header.title
                              : 'placeholder title'}
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        key={`${row._id}_trainings`}
                        style={{ padding: '5px' }}
                      >
                        {row.header.trainings.map(
                          (training) => `[${training}]`,
                        )}
                      </TableCell>
                      <TableCell
                        key={`${row._id}_passlevel`}
                        style={{ padding: '5px' }}
                      >
                        <Tooltip
                          title={`${
                            Date.parse(row.header.lastpass) +
                              testIntervalsInSecs[row.header.passlevel] * 1000 <
                            Date.parse(new Date().toISOString())
                              ? 'Up for test since'
                              : 'Next test in'
                          } ${timeDiffCalc(
                            Date.parse(row.header.lastpass) +
                              testIntervalsInSecs[row.header.passlevel] * 1000,
                            Date.parse(new Date().toISOString()),
                          )}`}
                          arrow
                        >
                          <div>{testIntervals[row.header.passlevel]}</div>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        key={`${row._id}_solution`}
                        style={{ padding: '5px' }}
                      >
                        <Tooltip
                          title={
                            row.header.type === FLASHCARD_TYPE_QUIZ
                              ? (row.solutions || [])
                                  .map((s) => s.text)
                                  .filter((t) => t && t.trim())
                                  .join(' · ') || '(Quiz)'
                              : row.solutions[0].text
                          }
                          arrow
                        >
                          <div>
                            {row.solutions !== undefined
                              ? row.header.type === FLASHCARD_TYPE_QUIZ
                                ? (() => {
                                    const joined = (row.solutions || [])
                                      .map((s) => s.text)
                                      .filter((t) => t && t.trim())
                                      .join(' · ');
                                    return joined.length > 40
                                      ? joined.substring(0, 40) + ' ...'
                                      : joined || '(Quiz)';
                                  })()
                                : row.solutions[0].text.length > 40
                                  ? row.solutions[0].text.substring(0, 40) +
                                    ' ...'
                                  : row.solutions[0].text
                              : 'solution'}
                          </div>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        key={`${row._id}_type`}
                        style={{ padding: '5px' }}
                      >
                        {row.header.type}
                      </TableCell>
                      <TableCell
                        key={`${row._id}_edit`}
                        style={{ padding: '5px' }}
                      >
                        <IconButton
                          onClick={() => setCurrentId(row._id)}
                          aria-label="edit button"
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell
                        key={`${row._id}_delete`}
                        style={{ padding: '5px' }}
                      >
                        <IconButton
                          onClick={() => {
                            dispatch(deleteFlashcard(row._id));
                          }}
                          aria-label="edit button"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
      />
    </Card>
  );
}

export default Flashcardselection;
